import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	Avatar,
	Box,
	Flex,
	Icon,
	Input,
	InputGroup,
	InputLeftElement,
	InputRightElement,
	Text,
	Button,
	IconButton,
	VStack,
	Divider,
	Image,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { motion, AnimatePresence } from 'framer-motion';
import { FaLock, FaUser } from 'react-icons/fa';
import { verifyPassword } from '../../Api/authApi';
import { useAuthenticatedUserInfo } from '../../services/Contexts/AuthenticatedUserInfoContext';
import { useCompanySettings } from '../../hooks/useCompany';
import UserImage from '../../assets/EWS-logo-trans.png';
import DefaultAuthBg from '../../assets/crm.webp';
import { getSessionLockMessage } from '../../constants/sessionIdle';
import SessionLockCountdown from './SessionLockCountdown';

const GREEN_SHAPE = '#8fbc5a';
const GREEN_SHAPE_LIGHT = '#b8d88e';
const HEADING_COLOR = '#1e3a5f';
const SUBTEXT_COLOR = '#64748b';
const INPUT_BORDER = '#e2e8f0';
const LINK_COLOR = '#d97706';
const BTN_GRADIENT = 'linear-gradient(90deg, #0f766e 0%, #16a34a 100%)';
const BTN_GRADIENT_HOVER = 'linear-gradient(90deg, #0d9488 0%, #22c55e 100%)';

const blockClipboard = (event) => {
	event.preventDefault();
};

const purgeInputValue = (inputRef, setValue) => {
	if (inputRef.current?.value) {
		inputRef.current.value = '';
		setValue('');
	}
};

const DotGrid = () => (
	<Box
		position='absolute'
		top='6%'
		right='8%'
		w='120px'
		h='120px'
		opacity={0.35}
		pointerEvents='none'
		style={{
			backgroundImage:
				'radial-gradient(circle, #4a7c2f 1.5px, transparent 1.5px)',
			backgroundSize: '14px 14px',
		}}
	/>
);

const OrganicShape = ({ top, bottom, left, right, size, color, rotate = 0 }) => (
	<Box
		position='absolute'
		top={top}
		bottom={bottom}
		left={left}
		right={right}
		w={size}
		h={size}
		pointerEvents='none'
		style={{
			background: color,
			borderRadius: '42% 58% 61% 39% / 48% 42% 58% 52%',
			transform: `rotate(${rotate}deg)`,
			filter: 'blur(0.5px)',
		}}
	/>
);

const SessionLockOverlay = ({
	displayName,
	email,
	profileImageSrc,
	onUnlock,
	onSignOut,
}) => {
	const { companyId } = useAuthenticatedUserInfo();
	const { data: companySettingsResponse } = useCompanySettings(companyId);

	const [password, setPassword] = useState('');
	const [error, setError] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [passwordFieldReady, setPasswordFieldReady] = useState(false);
	const passwordInputRef = useRef(null);

	const passwordFieldId = useMemo(
		() => `unlock-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`,
		[],
	);

	useEffect(() => {
		setPassword('');
		setPasswordFieldReady(false);

		const timers = [0, 50, 150, 400, 800, 1200].map((delay) =>
			window.setTimeout(() => purgeInputValue(passwordInputRef, setPassword), delay),
		);

		return () => timers.forEach((timer) => window.clearTimeout(timer));
	}, []);

	useEffect(() => {
		if (passwordFieldReady) return undefined;

		const interval = window.setInterval(() => {
			purgeInputValue(passwordInputRef, setPassword);
		}, 200);

		return () => window.clearInterval(interval);
	}, [passwordFieldReady]);

	const enablePasswordInput = () => {
		if (passwordFieldReady) return;
		setPasswordFieldReady(true);
		setPassword('');
		purgeInputValue(passwordInputRef, setPassword);
	};

	const handleAutofillAnimation = (event) => {
		if (event.animationName === 'sessionLockAutofillStart') {
			purgeInputValue(passwordInputRef, setPassword);
		}
	};

	const branding = useMemo(() => {
		const settings = companySettingsResponse?.data?.settings;
		const rootBranding = settings?.branding || {};
		const auth = rootBranding.auth || {};
		return {
			authBackgroundImage: auth.authBackgroundImage || null,
			panelBackgroundColor:
				auth.panelBackgroundColor ||
				rootBranding.sidebarBackgroundColor ||
				'linear-gradient(135deg, #1657a3 60%, #289c86 100%)',
			formCustomMessageTextColor:
				auth.formCustomMessageTextColor || auth.panelTextColor || HEADING_COLOR,
			formCompanyNameTextColor:
				auth.formCompanyNameTextColor ||
				auth.formCustomMessageTextColor ||
				HEADING_COLOR,
			logo: rootBranding.logo || null,
			companyName: companySettingsResponse?.data?.companyName || '',
		};
	}, [companySettingsResponse]);

	const panelImage = branding.authBackgroundImage || DefaultAuthBg;
	const headingColor = branding.formCompanyNameTextColor || HEADING_COLOR;
	const lockMessage = getSessionLockMessage();
	const title = displayName ? `Welcome back, ${displayName.split(' ')[0]}` : 'Welcome back';

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (!password.trim() || isSubmitting) return;

		setIsSubmitting(true);
		setError('');

		try {
			await verifyPassword(password);
			setPassword('');
			onUnlock();
		} catch (submitError) {
			setError(
				submitError?.response?.data?.msg ||
					'Incorrect password. Please try again.',
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Box
			data-session-lock-root
			position='fixed'
			inset={0}
			zIndex={9999}
			overflow='hidden'
			overscrollBehavior='none'>
			{/* Frosted backdrop — blurs the app behind */}
			<Box
				position='fixed'
				inset={0}
				pointerEvents='none'
				style={{
					backdropFilter: 'blur(16px) saturate(1.15)',
					WebkitBackdropFilter: 'blur(16px) saturate(1.15)',
					background: 'rgba(244, 246, 239, 0.55)',
				}}
			/>

			<Box
				position='relative'
				zIndex={1}
				minH='100vh'
				display='flex'
				alignItems='center'
				justifyContent='center'
				px={{ base: 4, md: 6 }}
				py={{ base: 6, md: 10 }}>
			<OrganicShape
				top='-80px'
				right='-60px'
				size='280px'
				color={GREEN_SHAPE}
				rotate={12}
			/>
			<OrganicShape
				bottom='-100px'
				left='-80px'
				size='320px'
				color={GREEN_SHAPE_LIGHT}
				rotate={-18}
			/>
			<DotGrid />

			<motion.div
				initial={{ opacity: 0, y: 24, scale: 0.97 }}
				animate={{ opacity: 1, y: 0, scale: 1 }}
				transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
				style={{ width: '100%', maxWidth: '920px', position: 'relative', zIndex: 2 }}>
				<Flex
					bg='white'
					borderRadius='2xl'
					overflow='hidden'
					minH={{ base: 'auto', md: '520px' }}
					boxShadow='0 24px 60px rgba(30, 58, 95, 0.14), 0 8px 24px rgba(30, 58, 95, 0.08)'
					direction={{ base: 'column', md: 'row' }}>
					{/* Mobile image strip */}
					<Box
						display={{ base: 'block', md: 'none' }}
						h='200px'
						w='full'
						order={{ base: 0, md: 1 }}
						flexShrink={0}
						style={{
							backgroundImage: `url(${panelImage})`,
							backgroundSize: 'cover',
							backgroundPosition: 'center',
						}}
					/>

					{/* Left — form */}
					<Box
						flex='1'
						order={{ base: 1, md: 0 }}
						px={{ base: 6, md: 10 }}
						py={{ base: 8, md: 10 }}
						display='flex'
						flexDirection='column'
						justifyContent='center'>
						<VStack align='stretch' spacing={6} maxW='380px' mx='auto' w='full'>
							{branding.logo ? (
								<Box textAlign='center'>
									<Image
										src={branding.logo}
										alt={branding.companyName || 'Company logo'}
										maxH='48px'
										objectFit='contain'
										w='auto'
										mx='auto'
										display='inline-block'
									/>
								</Box>
							) : null}

							<Box textAlign='center'>
								<Text
									fontSize={{ base: 'xl', md: '2xl' }}
									fontWeight='bold'
									lineHeight='short'
									mb={2}
									style={{ color: headingColor }}>
									{title}
								</Text>
								<Text fontSize='sm' lineHeight='tall' style={{ color: SUBTEXT_COLOR }}>
									{lockMessage}
								</Text>
							</Box>

							<SessionLockCountdown onExpired={onSignOut} />

							<Box as='form' onSubmit={handleSubmit} autoComplete='off'>
								<VStack spacing={4} align='stretch'>
									<Box>
										<InputGroup size='lg'>
											<InputLeftElement h='full' pl={1}>
												<Avatar
													boxSize='28px'
													src={profileImageSrc || UserImage}
													name={displayName || email}
													icon={<FaUser size={12} />}
												/>
											</InputLeftElement>
											<Input
												name='session-lock-identity'
												autoComplete='off'
												value={email || ''}
												isReadOnly
												tabIndex={-1}
												pl='3rem'
												h={12}
												borderRadius='lg'
												fontSize='sm'
												style={{
													border: `1px solid ${INPUT_BORDER}`,
													background: '#fafafa',
													color: '#334155',
												}}
											/>
										</InputGroup>
									</Box>

									<Box>
										<InputGroup size='lg'>
											<InputLeftElement h='full' pointerEvents='none'>
												<Icon as={FaLock} boxSize={4} style={{ color: '#94a3b8' }} />
											</InputLeftElement>
											<Input
												ref={passwordInputRef}
												id={passwordFieldId}
												name={passwordFieldId}
												type='text'
												inputMode='text'
												placeholder={
													passwordFieldReady
														? 'Type your password'
														: 'Click here to type your password'
												}
												value={password}
												autoComplete='off'
												autoCorrect='off'
												autoCapitalize='off'
												spellCheck={false}
												data-lpignore='true'
												data-1p-ignore='true'
												data-bwignore='true'
												data-form-type='other'
												readOnly={!passwordFieldReady}
												onPointerDown={enablePasswordInput}
												onFocus={enablePasswordInput}
												onPaste={blockClipboard}
												onCopy={blockClipboard}
												onCut={blockClipboard}
												onDrop={blockClipboard}
												onContextMenu={blockClipboard}
												onAnimationStart={handleAutofillAnimation}
												onChange={(event) => {
													if (!passwordFieldReady) return;
													setPassword(event.target.value);
													if (error) setError('');
												}}
												pl='2.75rem'
												pr='3rem'
												h={12}
												borderRadius='lg'
												fontSize='sm'
												isDisabled={isSubmitting}
												style={{
													border: `1px solid ${INPUT_BORDER}`,
													...(showPassword
														? {}
														: {
																WebkitTextSecurity: 'disc',
															}),
												}}
												sx={{
													'&:-webkit-autofill': {
														animationName: 'sessionLockAutofillStart',
														boxShadow: '0 0 0 1000px white inset',
													},
													'@keyframes sessionLockAutofillStart': {
														from: { opacity: 1 },
														to: { opacity: 1 },
													},
												}}
												_focus={{
													borderColor: '#0f766e',
													boxShadow:
														'0 0 0 3px rgba(15, 118, 110, 0.15)',
												}}
											/>
											<InputRightElement h='full' pr={2}>
												<IconButton
													size='sm'
													variant='ghost'
													icon={
														showPassword ? <ViewOffIcon /> : <ViewIcon />
													}
													onClick={() => setShowPassword((prev) => !prev)}
													isDisabled={isSubmitting || !passwordFieldReady}
													style={{ color: '#94a3b8' }}
												/>
											</InputRightElement>
										</InputGroup>
										<Text
											fontSize='xs'
											mt={2}
											mb={0}
											style={{ color: SUBTEXT_COLOR }}>
											For security, type your password manually. Saved passwords,
											paste, and autofill are disabled.
										</Text>
									</Box>

									<AnimatePresence>
										{error ? (
											<motion.div
												initial={{ opacity: 0, height: 0 }}
												animate={{ opacity: 1, height: 'auto' }}
												exit={{ opacity: 0, height: 0 }}>
												<Text
													fontSize='sm'
													textAlign='center'
													mb={0}
													style={{ color: '#dc2626' }}>
													{error}
												</Text>
											</motion.div>
										) : null}
									</AnimatePresence>

									<Button
										type='submit'
										w='full'
										h={12}
										borderRadius='lg'
										color='white'
										fontWeight='semibold'
										fontSize='md'
										isLoading={isSubmitting}
										loadingText='Unlocking...'
										isDisabled={!password.trim() || isSubmitting || !passwordFieldReady}
										_hover={{
											transform: 'translateY(-1px)',
											boxShadow: '0 10px 28px rgba(22, 163, 74, 0.35)',
										}}
										_active={{ transform: 'translateY(0)' }}
										transition='all 0.2s'
										style={{ background: BTN_GRADIENT }}
										onMouseEnter={(e) => {
											if (!e.currentTarget.disabled) {
												e.currentTarget.style.background = BTN_GRADIENT_HOVER;
											}
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.background = BTN_GRADIENT;
										}}>
										Unlock session
									</Button>
								</VStack>
							</Box>

							<Flex align='center' gap={3}>
								<Divider flex={1} style={{ borderColor: '#e2e8f0' }} />
								<Text fontSize='sm' mb={0} style={{ color: SUBTEXT_COLOR }}>
									or
								</Text>
								<Divider flex={1} style={{ borderColor: '#e2e8f0' }} />
							</Flex>

							<Button
								variant='link'
								fontSize='sm'
								fontWeight='medium'
								onClick={onSignOut}
								alignSelf='center'
								_hover={{ textDecoration: 'underline' }}
								style={{ color: LINK_COLOR }}>
								Sign out instead
							</Button>
						</VStack>
					</Box>

					{/* Right — branding image */}
					<Box
						flex='1'
						display={{ base: 'none', md: 'block' }}
						order={2}
						position='relative'
						minH='520px'
						style={{
							backgroundImage: `url(${panelImage})`,
							backgroundSize: 'cover',
							backgroundPosition: 'center',
						}}>
						{!branding.authBackgroundImage ? (
							<Box
								position='absolute'
								inset={0}
								style={{
									background:
										typeof branding.panelBackgroundColor === 'string' &&
										branding.panelBackgroundColor.includes('gradient')
											? branding.panelBackgroundColor
											: `linear-gradient(160deg, ${branding.panelBackgroundColor || '#1657a3'} 0%, #289c86 100%)`,
									opacity: branding.authBackgroundImage ? 0 : 0.25,
								}}
							/>
						) : null}
					</Box>
				</Flex>
			</motion.div>
			</Box>
		</Box>
	);
};

export default SessionLockOverlay;
